import { combineReducers } from 'redux'
import { handleActions } from 'redux-actions'
import _ from 'lodash'
import update from 'react-addons-update'
import moment from 'moment-timezone'
import { reducer as formReducer } from 'redux-form'
import { ROOM_EXTEND_DURATION, ROOM_HOLD_DURATION } from '../constants'
import { getObjFromCache, updateCollection } from './stateUtils'
import { getObjVal, isEmpty } from '../utils'
import { loadingBarReducer } from 'react-redux-loading-bar'
import flaggedContactReducer from './reducers/flaggedContactReducer'
import dashboardReducer from './reducers/dashboardReducer'
import { searchParamsReducer } from './modules/searchParamsModule'
import { errorDialogReducer } from './modules/errorDialogModule'
import { parseDate } from '../utils/dateUtils'
import { roomSearchResultsReducer } from './modules/searchResultsModule'
import { timeZoneOffsetReducer } from './modules/timeZoneOffsetModule'
import { roomTransferModule } from './modules/roomTransferModule'
import { paymentReducer } from './modules/paymentModule'

const roomPricesReducer = handleActions(
  {
    INIT_PRICES: (state, action) => {
      return action.payload
    }
  },
  []
)

function mergeRooms(rooms, newRooms) {
  return _.merge({}, rooms, newRooms)
}

const authenticationInitialValue = {
  authenticated: false,
  username: 'guest',
  level: 0,
  permissions: {}
}
const authentication = handleActions(
  {
    GUEST_USER: (state, action) => {
      return authenticationInitialValue
    },
    AUTH_USER: (state, action) => {
      return {
        ...state,
        ...action.payload
      }
    }
  },
  authenticationInitialValue
)

const bookingDetailsReducer = handleActions(
  {
    CHANGE_BOOKING_STATUS_ENTRY: (state, action) => {
      return { currentState: action.payload }
    },
    LOAD_STATUS_FROM_CACHE: (state, action) => {
      let bookingDetails = getObjFromCache('bookingDetails')
      if (bookingDetails) {
        return { currentState: bookingDetails.currentState }
      } else {
        return { ...state }
      }
    }
  },
  {
    currentState: 'NONE'
  }
)
const bookedDataReducer = handleActions(
  {
    BOOK_ROOMS_SUCCESS: (state, action) => {
      return {
        ...state,
        ...action.payload
      }
    }
  },
  {}
)
const roomReducer = handleActions(
  {
    FIND_ROOMS_SUCCESS: (state, action) => {
      let newRooms = _(action.payload)
        .map('rooms')
        .flatten()
        .keyBy('id')
        .value()
      delete newRooms[undefined]
      return mergeRooms(state, newRooms)
    },

    HOLD_ROOMS_SUCCESS: (state, action) => {
      let updateObj = {}
      action.payload.holds.forEach(hold => {
        updateObj[getObjVal(hold.roomId)] = { holdId: { $set: hold.holdId } }
      })
      let newState = update(state, updateObj)
      return newState
    },

    GET_ALL_ROOMS_SUCCESS: (state, action) => {
      delete action.payload.rooms[undefined]
      let newRooms = _.keyBy(action.payload.rooms, 'id')
      let newState = mergeRooms(state, newRooms)
      return newState
    },
    FIND_LOCAL_ROOMS: (state, action) => {
      let rooms = localStorage.getItem('rooms')
      if (!isEmpty(rooms) && Object.keys(rooms).length > 0) {
        return mergeRooms(state, JSON.parse(rooms))
      } else {
        return state
      }
    },

    UPDATE_ROOM_SUCCESS: (state, action) => {
      return updateCollection(state, action.payload)
    }
  },
  {}
)

// State value to check the history of pages
const history = handleActions(
  {
    SEARCH_PAGE: (state, action) => {
      return {
        prevPage: 'SEARCH'
      }
    },
    RESULTS_PAGE: (state, action) => {
      return {
        prevPage: 'RESULTS'
      }
    },
    GUEST_ENTRY_PAGE: (state, action) => {
      return {
        prevPage: 'GUEST_ENTRY'
      }
    },
    PAYMENT_PAGE: (state, action) => {
      return {
        prevPage: 'PAYMENT'
      }
    }
  },
  {
    prevPage: ''
  }
)

const visitReducer = handleActions(
  {
    GET_VISITS_SUCCESS: (state, action) => {
      let newVisits = _.mapValues(action.payload.entities.visits, visit => ({
        ...visit,
        checkInDate: parseDate(visit.checkInDate),
        checkOutDate: parseDate(visit.checkOutDate)
      }))
      return {
        ...state,
        ...newVisits
      }
    },

    UPDATE_VISIT_SUCCESS: (state, action) => {
      return updateCollection(state, action.payload)
    }
  },
  {}
)

const guestReducer = handleActions(
  {
    GET_VISITS_SUCCESS: (state, action) => {
      let newGuests = action.payload.entities.contacts
      return {
        ...state,
        ...newGuests
      }
    }
  },
  {}
)

const holdStatusReducer = handleActions(
  {
    HOLD_ROOMS_SUCCESS: (state, action) => {
      let expireTime = moment().add(ROOM_HOLD_DURATION, 'milliseconds')
      let timeLeft = moment.duration(expireTime.diff(moment()))
      let warnTime = moment().add(ROOM_EXTEND_DURATION, 'milliseconds')
      return {
        status: 'active',
        expireTime,
        timeLeft,
        warnTime,
        show: false,
        popupShown: false,
        closedPopup: false
      }
    },

    HOLD_EXPIRED: (state, action) => {
      return {
        status: 'expired',
        expireTime: null,
        timeLeft: null,
        warnTime: null,
        popupShown: false,
        closedPopup: false
      }
    },
    CANCELLED: (state, action) => {
      return {
        status: 'cancelled',
        expireTime: null,
        timeLeft: null,
        warnTime: null,
        popupShown: false,
        closedPopup: false
      }
    },

    HOLD_TIMER_TICK: (state, action) => {
      return {
        ...state,
        popupShown: false,
        timeLeft: moment.duration(state.expireTime.diff(moment()))
      }
    },

    HOLD_STATUS_RESET: (state, action) => {
      return {
        status: 'inactive',
        expireTime: null,
        timeLeft: null,
        warnTime: null,
        popupShown: false,
        closedPopup: false
      }
    },
    OPEN_TIMEOUT_EXTEND_DIALOG: (state, action) => {
      return {
        ...state,
        show: true,
        popupShown: true,
        closedPopup: false
      }
    },
    CLOSE_TIMEOUT_EXTEND_DIALOG: (state, action) => {
      return {
        ...state,
        show: false,
        popupShown: false,
        closedPopup: true
      }
    },
    EXTEND_TIME: (state, action) => {
      let expireTime = moment().add(ROOM_HOLD_DURATION, 'milliseconds')
      let timeLeft = moment.duration(expireTime.diff(moment()))
      let warnTime = moment().add(ROOM_EXTEND_DURATION, 'milliseconds')
      return {
        ...state,
        expireTime,
        timeLeft,
        warnTime,
        show: false,
        closedPopup: false
      }
    },
    LOAD_HOLD_STATUS: (state, action) => {
      let holdStatus = getObjFromCache('holdStatus')
      return {
        status: holdStatus.status,
        expireTime: moment(holdStatus.expireTime),
        timeLeft: moment.duration(holdStatus.timeLeft),
        warnTime: moment(holdStatus.warnTime),
        show: holdStatus.show,
        popupShown: holdStatus.popupShown,
        closedPopup: holdStatus.closedPopup
      }
    }
  },
  {
    status: 'inacive',
    expireTime: null,
    timeLeft: null,
    warnTime: null,
    show: false,
    popupShown: false,
    closedPopup: false
  }
)

export default combineReducers({
  common: combineReducers({
    authentication: authentication,
    history: history,
    formErrors: errorDialogReducer,
    roomPrices: roomPricesReducer,
    bookingDetails: bookingDetailsReducer
  }),
  //routing: routerReducer,
  rooms: roomReducer,
  visits: visitReducer,
  guests: guestReducer,
  dashboard: dashboardReducer,
  roomSearch: combineReducers({
    params: searchParamsReducer,
    results: roomSearchResultsReducer,
    holdStatus: holdStatusReducer,
    bookedData: bookedDataReducer,
    flaggedContact: flaggedContactReducer
  }),
  payment: paymentReducer,
  form: formReducer,
  loadingBar: loadingBarReducer,
  timeZone: timeZoneOffsetReducer,
  roomTransfer: roomTransferModule.reducer
})



// WEBPACK FOOTER //
// ./src/redux/reducers.js